import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import Avatar from '@mui/material/Avatar';
import useMediaQuery from '@mui/material/useMediaQuery';
import CustomButton from '../../../components/common/CustomButton';

export function FamilyTreeSection() {
  const { palette, typography, breakpoints } = useTheme();
  const isSmallScreen = useMediaQuery(breakpoints.down('sm'));
  const { t } = useTranslation('landing');
  const navigate = useNavigate();
  return (
    <Box
      id="family-tree"
      component="section"
      sx={{
        width: '100%',
        bgcolor: palette.background.paper,
      }}
    >
      <Box
        sx={{
          width: '100%',
          textAlign: 'center',
          px: { xs: 2, md: 6 },
          py: { xs: 4, md: 6 },
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontSize: {
              xs: typography.h3.fontSize,
              md: typography.h1.fontSize,
            },
            fontWeight: {
              xs: typography.h3.fontWeight,
              md: typography.h1.fontWeight,
            },
            color: palette.primary.main,
            py: 2,
            textAlign: 'center',
          }}
        >
          Plant Your Family Tree. Watch Your Legacy Grow.
        </Typography>
        <Typography
          sx={{
            color: palette.text.primary,
            fontSize: {
              xs: typography.body1.fontSize,
              md: '20px',
            },
            py: { xs: '0', md: 2 },
            lineHeight: '1.75',
            textAlign: 'center',
          }}
        >
          Create a living record of your family&apos;s journey from ancestral
          villages to the diaspora. Trace the lineage, honour the stories, and
          map the relationships that make your family unique. Connect the dots
          between who you are, where you are, and where you came from.
        </Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
          maxWidth: 760,
          mx: 'auto',
          mb: { xs: 4, md: 5 },
          px: { xs: 2, md: 4 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 1,
            p: { xs: 3 },
            backgroundColor: palette.background.default,
            borderRadius: 5,
          }}
        >
          <Typography
            component="h3"
            sx={{
              color: palette.primary.main,
              fontSize: {
                xs: typography.h5.fontSize,
                md: typography.h4.fontSize,
              },
            }}
          >
            Already started your family's story elsewhere?
          </Typography>
          <Typography
            sx={{
              color: palette.text.secondary,
              fontSize: {
                xs: typography.body2.fontSize,
                md: typography.body1.fontSize,
              },
              lineHeight: 1.6,
              maxWidth: 620,
            }}
          >
            Bring it here. Import a GEDCOM file and pick up right where you left
            off.
          </Typography>
          <CustomButton
            label="Import a GEDCOM"
            onClick={() => navigate('/app')}
            variant="contained"
            color="info"
            sx={{
              mt: 1,
              '&:hover': { color: 'white', border: 'none !important' },
            }}
          />
        </Box>
      </Box>
      <Grid container alignItems="center" justifyContent="center" pb={5}>
        <Grid size={{ xs: 12, sm: 12, md: 6 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              backgroundColor: palette.secondary.light,
              border: `1.2px solid ${palette.secondary.dark}`,
              borderRadius: 5,
              mx: { xs: 2 },
            }}
          >
            <Typography
              sx={{
                textAlign: 'center',
                fontSize: {
                  xs: typography.h4.fontSize,
                  md: typography.h2.fontSize,
                },
                fontWeight: {
                  xs: typography.h3.fontWeight,
                  md: typography.h1.fontWeight,
                },
                color: palette.text.primary,
                pt: { xs: 2, sm: 4 },
              }}
            >
              {t('landing:instructions.gettingStarted')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                color: palette.text.primary,
                p: 4,
              }}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: palette.secondary.dark }}>1</Avatar>
                <Box>
                  <Typography fontSize="1.2rem" fontWeight={600}>
                    {t('landing:instructions.steps.stepOneTitle')}
                  </Typography>
                  <Typography variant="body2">
                    {t('landing:instructions.steps.stepOne')}
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: palette.secondary.dark }}>2</Avatar>
                <Box>
                  <Typography fontSize="1.2rem" fontWeight={600}>
                    {t('landing:instructions.steps.stepTwoTitle')}
                  </Typography>
                  <Typography variant="body2">
                    {t('landing:instructions.steps.stepTwo')}
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: palette.secondary.dark }}>3</Avatar>
                <Box>
                  <Typography fontSize="1.2rem" fontWeight={600}>
                    {t('landing:instructions.steps.stepThreeTitle')}
                  </Typography>
                  <Typography variant="body2">
                    {t('landing:instructions.steps.stepThree')}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: palette.secondary.dark }}>4</Avatar>
                <Box>
                  <Typography fontSize="1.2rem" fontWeight={600}>
                    {t('landing:instructions.steps.stepFourTitle')}
                  </Typography>
                  <Typography variant="body2">
                    {t('landing:instructions.steps.stepFour')}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: palette.secondary.dark }}>5</Avatar>
                <Box>
                  <Typography fontSize="1.2rem" fontWeight={600}>
                    {t('landing:instructions.steps.stepFiveTitle')}
                  </Typography>
                  <Typography variant="body1">
                    {t('landing:instructions.steps.stepFive')}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'center',
                alignItems: 'center',
                py: 3,
                gap: 2,
              }}
            >
              <CustomButton
                label="Start Building Your Tree"
                variant="contained"
                color="secondary"
                sx={{
                  color() {
                    return palette.primary.main;
                  },
                  '&:hover': {
                    color: 'white',
                  },
                }}
                onClick={() => navigate('/auth/signup')}
                fullWidth={isSmallScreen ? true : false}
              />
              <CustomButton
                label="Explore Sample Family Tree"
                variant="outlined"
                color="primary"
                sx={{
                  '&:hover': {
                    color: 'white',
                    border: 'none !important',
                  },
                }}
                onClick={() => navigate('/sample-tree')}
                fullWidth={isSmallScreen ? true : false}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
